import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '../api.js'
import { AuthCard, authCardStyles as styles } from '../auth-shell/AuthCard.js'
import { useAcceptTenantInvite } from '../tenant-team/queries.js'
import { Button, Input } from '../ui/index.js'

/**
 * `LGH-040`. Where an invited team member lands — no session required (the
 * endpoint's own design: accepting an invite is how the session begins).
 * Reached from whatever channel the invite arrived on; this repo has no
 * "compose the invite message" concern, only the landing page it links to.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `DES-001`, presentation only. This was the worst screen in the console and
 * the one with the least forgiving audience: a person who has never seen this
 * product, opening a link from a WhatsApp message, on a phone.
 *
 * At 375px the two wrapping `<label>`s and their two bare `<input>`s collapsed
 * into a single run of text — *"Your name [box] Confirmation code [box] Join"*
 * — with "Confirmation" and "code" broken over two lines. Live-checked at
 * that width, not inferred.
 *
 * What is still missing here is not markup and is not fixed here: the invite
 * response carries no tenant name and no role, so this screen cannot say which
 * company you are joining or what you are being given — and `R-091` makes the
 * role someone else's choice, taken before you arrive. Filed to `3L`
 * (`REQ-design-the-customer-console-ux-audit`, finding L4).
 * ───────────────────────────────────────────────────────────────────────────
 */
export function AcceptTenantInvitePage() {
  const { inviteId } = useParams<{ inviteId: string }>()
  const navigate = useNavigate()
  const [otp, setOtp] = useState('')
  const [name, setName] = useState('')
  const accept = useAcceptTenantInvite()

  if (inviteId === undefined) {
    return (
      <AuthCard documentTitle="Join your team · Pruvato" title="This link is incomplete">
        <p role="alert" className={styles['error']}>
          The invite link is missing its code. Ask whoever invited you to send it again.
        </p>
      </AuthCard>
    )
  }

  return (
    <AuthCard
      documentTitle="Join your team · Pruvato"
      title="Join your team"
      lede="Your colleague has added you to their Pruvato account. Confirm the code they sent you on WhatsApp and tell us your name."
      footnote="Pruvato turns your customers' WhatsApp and email requests into quotes your team approves."
    >
      <form
        className={styles['form']}
        onSubmit={(event) => {
          event.preventDefault()
          accept.mutate(
            { inviteId, otp: otp.trim(), name: name.trim() },
            {
              onSuccess: () => {
                navigate('/customer/team', { replace: true })
              },
            },
          )
        }}
      >
        <Input
          label="Your name"
          autoComplete="name"
          autoFocus
          hint="How you'll appear to the rest of the team."
          placeholder="e.g. Priya Nair"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
        />
        <Input
          label="Six-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={otp}
          onChange={(event) => {
            setOtp(event.target.value)
          }}
          {...(accept.isError
            ? {
                error:
                  accept.error instanceof ApiError
                    ? accept.error.message
                    : 'That code was not accepted.',
              }
            : {})}
        />
        <div className={styles['actions']}>
          <Button
            type="submit"
            variant="primary"
            disabled={name.trim() === '' || otp.trim() === ''}
            loading={accept.isPending}
          >
            {accept.isPending ? 'Joining…' : 'Join'}
          </Button>
        </div>
      </form>
    </AuthCard>
  )
}
